/**
 * 날짜 유틸리티 함수들
 * LocalDateTime.toString() 형태의 문자열을 처리
 */

/**
 * LocalDateTime의 toString() 결과를 JavaScript Date 객체로 변환
 * 예: "2024-01-15T10:30:45.123456" -> Date 객체
 * @param {string} localDateTimeString - LocalDateTime.toString() 결과
 * @returns {Date|null} JavaScript Date 객체 또는 null
 */
export const parseLocalDateTime = (localDateTimeString) => {
  if (!localDateTimeString || typeof localDateTimeString !== 'string') {
    return null;
  }

  try {
    // LocalDateTime.toString()은 ISO 8601 형태이므로 바로 파싱 가능
    // 다만 Z가 없을 수 있으므로 로컬 시간으로 처리
    const date = new Date(localDateTimeString);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.warn('Failed to parse LocalDateTime string:', localDateTimeString, error);
    return null;
  }
};

/**
 * LocalDateTime 문자열을 한국 날짜 형식으로 포매팅
 * @param {string} localDateTimeString - LocalDateTime.toString() 결과
 * @param {boolean} includeTime - 시간 포함 여부 (기본값: false)
 * @returns {string} 포매팅된 날짜 문자열
 */
export const formatLocalDateTime = (localDateTimeString, includeTime = false) => {
  const date = parseLocalDateTime(localDateTimeString);
  if (!date) return '-';

  // LocalDateTime은 이미 로컬 시간이므로 timeZone 변환을 하지 않음
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
    // timeZone 옵션 제거 - 서버에서 이미 로컬 시간으로 보내줌
  };

  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.second = '2-digit';
  }

  return date.toLocaleDateString('ko-KR', options);
};

/**
 * LocalDateTime 문자열을 상대적 시간으로 표시 (예: "3분 전", "2시간 전")
 * @param {string} localDateTimeString - LocalDateTime.toString() 결과
 * @returns {string} 상대적 시간 문자열
 */
export const formatRelativeTime = (localDateTimeString) => {
  const date = parseLocalDateTime(localDateTimeString);
  if (!date) return '-';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return '방금 전';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}분 전`;
  } else if (diffHours < 24) {
    return `${diffHours}시간 전`;
  } else if (diffDays < 7) {
    return `${diffDays}일 전`;
  } else {
    return formatLocalDateTime(localDateTimeString, false);
  }
};

/**
 * 두 LocalDateTime 문자열을 비교
 * @param {string} dateTime1 - 첫 번째 LocalDateTime 문자열
 * @param {string} dateTime2 - 두 번째 LocalDateTime 문자열
 * @returns {number} -1, 0, 1 (dateTime1이 더 이른 경우 -1, 같은 경우 0, 늦은 경우 1)
 */
export const compareLocalDateTime = (dateTime1, dateTime2) => {
  const date1 = parseLocalDateTime(dateTime1);
  const date2 = parseLocalDateTime(dateTime2);

  if (!date1 && !date2) return 0;
  if (!date1) return -1;
  if (!date2) return 1;

  if (date1.getTime() < date2.getTime()) return -1;
  if (date1.getTime() > date2.getTime()) return 1;
  return 0;
};

/**
 * LocalDateTime 문자열이 유효한지 검사
 * @param {string} localDateTimeString - 검사할 문자열
 * @returns {boolean} 유효한 날짜인지 여부
 */
export const isValidLocalDateTime = (localDateTimeString) => {
  return parseLocalDateTime(localDateTimeString) !== null;
};